'use client';

import { useState, useEffect, useRef } from 'react';
import { Play, Pause, Check, Lock, Mic } from 'lucide-react';
import VoiceTrainerModal from '../audio-book/voice-trainer-modal';

interface Voice {
  id: string;
  name: string;
  voiceId: string;
  description: string | null;
  sampleAudioUrl: string | null;
  provider: string;
}

interface StepVoiceProps {
  value: string;
  onChange: (voiceId: string) => void;
  isSubscriber: boolean;
  isPro?: boolean;
  language?: string;
}

// Voice vibe tags based on description
const getVibeTags = (description: string | null): string[] => {
  if (!description) return ['Professional'];
  const desc = description.toLowerCase();
  const tags: string[] = [];
  
  if (desc.includes('warm') || desc.includes('friendly')) tags.push('Warm');
  if (desc.includes('calm') || desc.includes('soothing')) tags.push('Calm');
  if (desc.includes('energetic') || desc.includes('upbeat')) tags.push('Energetic');
  if (desc.includes('british') || desc.includes('uk')) tags.push('British');
  if (desc.includes('american')) tags.push('American');
  
  return tags.length > 0 ? tags : ['Professional'];
};

export default function StepVoice({ value, onChange, isSubscriber, isPro = false, language = 'en' }: StepVoiceProps) {
  const [voices, setVoices] = useState<Voice[]>([]);
  const [loading, setLoading] = useState(true);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [showVoiceUpload, setShowVoiceUpload] = useState(false);
  const [voiceMappings, setVoiceMappings] = useState<{ [voiceId: string]: string[] }>({});
  const [customVoiceId, setCustomVoiceId] = useState<string | null>(null);
  const [customVoiceStatus, setCustomVoiceStatus] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationRef = useRef<number | null>(null);

  useEffect(() => {
    fetchVoices();
    fetchVoiceMappings();
    fetchCustomVoice();
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  const fetchVoices = async () => {
    try {
      const response = await fetch('/api/voices');
      const data = await response.json();
      setVoices(data.voices || []);
      
      // Set default voice if none selected
      if (!value && data.voices.length > 0) {
        onChange(data.voices[0].voiceId);
      }
    } catch (error) {
      console.error('Error fetching voices:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVoiceMappings = async () => {
    try {
      const response = await fetch('/api/admin/voice-mappings');
      if (response.ok) {
        const data = await response.json();
        const mappingsMap: { [voiceId: string]: string[] } = {};
        data.mappings.forEach((mapping: any) => {
          mappingsMap[mapping.voiceId] = mapping.languages;
        });
        setVoiceMappings(mappingsMap);
      } else if (response.status === 403) {
        // Silent fail for non-admin users - voice mappings are optional
        console.log('[Voice Mappings] Not available for non-admin users');
      } else {
        console.warn('[Voice Mappings] Error:', response.status, response.statusText);
      }
    } catch (error) {
      // Silent fail - voice mappings are optional
      console.log('[Voice Mappings] Not available');
    }
  };

  const fetchCustomVoice = async () => {
    try {
      const response = await fetch('/api/voice-training/me');
      if (response.ok) {
        const data = await response.json();
        console.log('[StepVoice] Voice training data:', data);
        
        if (data.userVoice?.status === 'ready' && data.userVoice?.elevenLabsVoiceId) {
          setCustomVoiceId(data.userVoice.elevenLabsVoiceId);
          setCustomVoiceStatus(data.userVoice.status);
          console.log('[StepVoice] Custom voice loaded:', data.userVoice.elevenLabsVoiceId);
        }
      }
    } catch (error) {
      console.log('[StepVoice] Error fetching custom voice:', error);
    }
  };

  // Filter voices by language compatibility
  const getFilteredVoices = () => {
    if (!language || Object.keys(voiceMappings).length === 0) {
      return voices; // Show all if no language selected or mappings not loaded
    }
    
    return voices.filter(voice => {
      const languages = voiceMappings[voice.id];
      if (!languages || languages.length === 0) {
        return true; // Show voices without mappings (backward compatibility)
      }
      return languages.includes(language);
    });
  };

  const filteredVoices = getFilteredVoices();
  
  // Prepend custom voice if available (always show custom voice regardless of language)
  const allVoices = customVoiceId && customVoiceStatus === 'ready'
    ? [
        {
          id: 'custom-voice',
          name: 'Your Voice',
          voiceId: customVoiceId,
          description: 'Your custom trained voice',
          sampleAudioUrl: null,
          provider: 'ELEVENLABS',
          isCustom: true,
        } as Voice & { isCustom?: boolean },
        ...filteredVoices,
      ]
    : filteredVoices;

  const playVoiceSample = (voice: Voice) => {
    if (!voice.sampleAudioUrl) return;

    // If already playing this voice, pause it
    if (playingId === voice.id && audioRef.current) {
      audioRef.current.pause();
      setPlayingId(null);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      return;
    }

    // Stop current audio if any
    if (audioRef.current) {
      audioRef.current.pause();
    }

    const audio = new Audio(voice.sampleAudioUrl);
    audioRef.current = audio;
    setPlayingId(voice.id);

    audio.onended = () => {
      setPlayingId(null);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };

    audio.onerror = () => {
      setPlayingId(null);
      alert('Error playing sample audio');
    };

    audio.play().catch((error) => {
      console.error('Error playing audio:', error);
      setPlayingId(null);
    });

    // Simple waveform visualization
    if (canvasRef.current && typeof window !== 'undefined') {
      const ctx = canvasRef.current.getContext('2d');
      if (ctx) {
        drawWaveform(ctx);
      }
    }
  };

  const drawWaveform = (ctx: CanvasRenderingContext2D) => {
    const canvas = ctx.canvas;
    const width = canvas.width;
    const height = canvas.height;

    const draw = () => {
      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = '#C5B4FF';
      
      // Simple animated bars
      const bars = 20;
      const barWidth = width / bars;
      
      for (let i = 0; i < bars; i++) {
        const barHeight = Math.random() * height * 0.6 + height * 0.2;
        ctx.fillRect(
          i * barWidth + barWidth * 0.2,
          (height - barHeight) / 2,
          barWidth * 0.6,
          barHeight
        );
      }

      animationRef.current = requestAnimationFrame(draw);
    };

    draw();
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
        Loading voices...
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '100%', width: '100%', margin: '0 auto' }}>
      <div style={{ 
        textAlign: 'center', 
        marginBottom: '2.5rem',
        animation: 'fadeInUp 0.6s ease-out',
      }}>
        <h2 style={{ 
          fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', 
          fontWeight: 800, 
          color: '#444',
          marginBottom: '0.75rem',
        }}>
          Choose Your Narrator Voice
        </h2>
        <p style={{ 
          fontSize: '1.125rem', 
          color: '#666',
          lineHeight: 1.6,
        }}>
          {isSubscriber 
            ? 'Select from our premium voice collection' 
            : 'Try our default voice, or subscribe for more choices!'}
        </p>
      </div>

      {/* Create with Your Own Voice Button - Visible to ALL users */}
      <button
        onClick={() => setShowVoiceUpload(true)}
        style={{
          width: '100%',
          padding: '20px',
          background: isPro 
            ? 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
            : 'linear-gradient(135deg, rgba(240, 147, 251, 0.5) 0%, rgba(245, 87, 108, 0.5) 100%)',
          border: 'none',
          borderRadius: '16px',
          color: 'white',
          fontSize: '1.125rem',
          fontWeight: 700,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
          transition: 'all 0.3s ease',
          marginBottom: '1.5rem',
          boxShadow: isPro ? '0 4px 16px rgba(240, 147, 251, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = isPro 
            ? '0 8px 24px rgba(240, 147, 251, 0.4)'
            : '0 4px 12px rgba(0, 0, 0, 0.15)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = isPro 
            ? '0 4px 16px rgba(240, 147, 251, 0.3)'
            : '0 2px 8px rgba(0, 0, 0, 0.1)';
        }}
      >
        <Mic size={24} />
        <span>Create with Your Own Voice</span>
        <span style={{
          background: 'rgba(255, 255, 255, 0.3)',
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '0.875rem',
          fontWeight: 600,
        }}>
          PRO
        </span>
      </button>

      {/* Waveform Canvas (hidden, used for visualization) */}
      <canvas 
        ref={canvasRef} 
        width="200" 
        height="60" 
        style={{ display: 'none' }}
      />

      {/* Voice Cards Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
        gap: '1.5rem',
      }}>
        {allVoices.map((voice, index) => {
          const isCustomVoice = 'isCustom' in voice && voice.isCustom;
          const isSelected = value === voice.voiceId;
          // Custom voice is never disabled, regular voices follow subscription logic
          const isDisabled = !isCustomVoice && (!isSubscriber && index > (customVoiceId ? 1 : 0));
          const isPlaying = playingId === voice.id;
          const vibeTags = getVibeTags(voice.description);

          return (
            <div
              key={voice.id}
              onClick={() => !isDisabled && onChange(voice.voiceId)}
              style={{
                background: isCustomVoice
                  ? (isSelected 
                      ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)'
                      : 'linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%)')
                  : (isSelected ? 'linear-gradient(135deg, rgba(197, 180, 255, 0.1) 0%, rgba(181, 242, 224, 0.1) 100%)' : 'white'),
                border: isCustomVoice
                  ? (isSelected ? '3px solid #667eea' : '2px solid #A78BFA')
                  : (isSelected ? '3px solid #C5B4FF' : '2px solid #e0d5f0'),
                borderRadius: 'var(--r-2xl, 28px)',
                padding: '1.5rem',
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                opacity: isDisabled ? 0.6 : 1,
                position: 'relative',
                minHeight: '48px', // Touch target
              }}
              onMouseEnter={(e) => {
                if (!isDisabled) {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 12px 32px rgba(197, 180, 255, 0.2)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
              role="radio"
              aria-checked={isSelected}
              aria-label={`${voice.name} voice`}
              tabIndex={isDisabled ? -1 : 0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  if (!isDisabled) {
                    onChange(voice.voiceId);
                  }
                }
              }}
            >
              {/* Lock icon for non-subscribers */}
              {isDisabled && (
                <div style={{
                  position: 'absolute',
                  top: '1rem',
                  right: '1rem',
                  background: 'rgba(0, 0, 0, 0.1)',
                  borderRadius: '50%',
                  padding: '8px',
                }}>
                  <Lock size={16} color="#888" />
                </div>
              )}

              {/* Selected checkmark */}
              {isSelected && !isDisabled && (
                <div style={{
                  position: 'absolute',
                  top: '1rem',
                  right: '1rem',
                  background: '#C5B4FF',
                  borderRadius: '50%',
                  padding: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <Check size={16} color="white" strokeWidth={3} />
                </div>
              )}

              {/* Avatar/Emoji */}
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #C5B4FF 0%, #B5F2E0 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '2rem',
                marginBottom: '1rem',
                animation: isPlaying ? 'mouth 0.5s ease-in-out infinite' : 'none',
              }}>
                🎤
              </div>

              {/* Voice Name */}
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: 700,
                color: '#444',
                marginBottom: '0.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
              }}>
                {voice.name}
                {isCustomVoice && (
                  <span style={{
                    padding: '0.125rem 0.5rem',
                    background: 'linear-gradient(135deg, #667eea, #764ba2)',
                    color: 'white',
                    borderRadius: '8px',
                    fontSize: '0.625rem',
                    fontWeight: 800,
                    letterSpacing: '0.05em',
                  }}>
                    PRO
                  </span>
                )}
              </h3>

              {/* Vibe Tags or Custom Voice Label */}
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '0.5rem',
                marginBottom: '1rem',
              }}>
                {isCustomVoice ? (
                  <div style={{
                    padding: '0.5rem 1rem',
                    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1))',
                    borderRadius: '10px',
                    fontSize: '0.875rem',
                    color: '#667eea',
                    fontWeight: 600,
                    textAlign: 'center',
                    border: '1px solid rgba(102, 126, 234, 0.2)',
                    width: '100%',
                  }}>
                    ✨ Your Custom Voice
                  </div>
                ) : (
                  vibeTags.map((tag) => (
                    <span
                      key={tag}
                      style={{
                        padding: '0.25rem 0.625rem',
                        background: 'rgba(197, 180, 255, 0.15)',
                        borderRadius: '12px',
                        fontSize: '0.8125rem',
                        fontWeight: 600,
                        color: '#7C3AED',
                      }}
                    >
                      {tag}
                    </span>
                  ))
                )}
              </div>

              {/* Voice Preview Button */}
              {voice.sampleAudioUrl && !isDisabled && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    playVoiceSample(voice);
                  }}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: isPlaying ? '#7C3AED' : 'white',
                    color: isPlaying ? 'white' : '#7C3AED',
                    border: `2px solid ${isPlaying ? '#7C3AED' : '#C5B4FF'}`,
                    borderRadius: '12px',
                    fontSize: '0.9375rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    transition: 'all 0.2s ease',
                    minHeight: '44px',
                  }}
                  aria-label={isPlaying ? 'Pause voice preview' : 'Play voice preview'}
                  aria-pressed={isPlaying}
                >
                  {isPlaying ? <Pause size={16} /> : <Play size={16} />}
                  {isPlaying ? 'Playing...' : 'Voice Preview'}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes mouth {
          0%, 100% {
            transform: scaleY(1);
          }
          50% {
            transform: scaleY(0.85);
          }
        }

        @media (prefers-reduced-motion: reduce) {
          [style*="animation"] {
            animation: none !important;
          }
        }
      `}</style>

      {/* Voice Trainer Modal - QR Code System */}
      <VoiceTrainerModal
        isOpen={showVoiceUpload}
        onClose={() => setShowVoiceUpload(false)}
        onComplete={() => {
          console.log('[StepVoice] Voice training completed! Refreshing custom voice...');
          setShowVoiceUpload(false);
          // Refresh custom voice to show the new trained voice
          fetchCustomVoice();
        }}
      />
    </div>
  );
}

